/**
 * token-picker-field.tsx — basic token-picker dropdown for D-050-05
 * adaptive token-vs-literal mode in the ABox properties panel.
 *
 * Reads tokens from `useStudioStore(s => s.currentProject?.designSystem)`
 * (CONTEXT D-050-05 Claude's Discretion — data source is planner discretion;
 * `designSystem` is the natural source). Renders a native <select> for ≤20
 * tokens; combobox-search for >20 tokens is future polish.
 *
 * Contract:
 *   - `value`    — the current tokenRef string (e.g., 'color.primary').
 *   - `onChange` — emits the new tokenRef string. `PropertyRow` wraps the
 *                  callback with `commitInstant({ tokenRef: <new> })` so the
 *                  canonical `{ tokenRef }` shape lands in `useDraftStore`
 *                  (preserves SC#2 invariant — token binding identity).
 *
 * Defensive token extraction — `designSystem` shape historically is
 * `Record<string, string>` (className patterns by token name) but future
 * shapes could carry an array of `{ name }` entries. The extractor accepts
 * both and degrades to an empty list if the shape is unrecognised; the
 * baseline tokenRef is always exposed as a "(current)" option entry so the
 * operator never sees a binding silently disappear.
 */
import { useStudioStore } from '@/store/studio-store'

interface TokenPickerFieldProps {
  label?: string
  value: string
  onChange: (tokenRef: string) => void
}

function extractTokenNames(designSystem: unknown): string[] {
  if (!designSystem) return []
  // Shape A — Record<string, string> (current v2 designSystem.json).
  if (
    typeof designSystem === 'object' &&
    !Array.isArray(designSystem) &&
    designSystem !== null &&
    !('tokens' in (designSystem as object))
  ) {
    return Object.keys(designSystem as Record<string, unknown>)
  }
  // Shape B — { tokens: Array<{ name } | string> } (potential future shape).
  const tokens = (designSystem as { tokens?: unknown }).tokens
  if (Array.isArray(tokens)) {
    return tokens
      .map((t) => (typeof t === 'string' ? t : (t as { name?: string }).name))
      .filter((s): s is string => typeof s === 'string')
  }
  return []
}

export function TokenPickerField({
  label,
  value,
  onChange,
}: TokenPickerFieldProps) {
  const designSystem = useStudioStore((s) => s.currentProject?.designSystem)
  const tokens = extractTokenNames(designSystem)

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs px-2 py-1 border border-input rounded bg-background"
        data-testid={`token-picker-${label ?? 'unnamed'}`}
      >
        {!tokens.includes(value) && value && (
          <option value={value}>{value} (current)</option>
        )}
        {tokens.length === 0 && !value && (
          <option value="">(no tokens available)</option>
        )}
        {tokens.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
    </div>
  )
}
